/**
 * DynamicGameRenderer — Wave 2 skeleton.
 *
 * Walks a validated GameSpec scene by scene. Computes score, streak, and
 * win flag. Emits typed analytics events at start and finish. Per-scene
 * UIs are placeholder stubs — they ship in Wave 3.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Heart, Trophy, Clock } from 'lucide-react';

import type {
  GameSpec,
  GameRunResult,
  Scene,
  SceneAttempt,
} from '@/types/gameSpec';
import { computeMaxScore, isWin, pointsForScene } from './scoring';
import { validateGameSpec } from './validator';
import { UnplayableNotice } from './UnplayableNotice';
import { SceneRouter } from './scenes/SceneRouter';
import { ResultScreen } from './ResultScreen';
import { trackEvent } from '@/lib/analytics/ga';

interface DynamicGameRendererProps {
  /** Raw spec from games.game_config — re-validated defensively. */
  spec: unknown;
  gameId: string;
  grade: string | number;
  onComplete: (result: GameRunResult) => void;
  onExit?: () => void;
  /** Optional: when set, the run was opened from a share link (Wave 5). */
  sharedFromUserId?: string | null;
}

import { isJsRuntimeConfig } from '@/types/jsGame';
import { JsGameSandbox } from '../runtime/JsGameSandbox';

export const DynamicGameRenderer = ({
  spec: rawSpec,
  gameId,
  grade,
  onComplete,
  onExit,
  sharedFromUserId,
}: DynamicGameRendererProps) => {
  // New engine: AI-generated JS games run in a sandboxed iframe.
  if (isJsRuntimeConfig(rawSpec)) {
    return (
      <JsGameSandbox
        config={rawSpec}
        gameId={gameId}
        grade={grade}
        onComplete={onComplete}
        onExit={onExit}
      />
    );
  }

  // Legacy scene-spec games — kept for back-compat with already-stored games.
  const validation = useMemo(() => validateGameSpec(rawSpec), [rawSpec]);

  if (validation.ok === false) {
    return <UnplayableNotice errors={validation.errors} onExit={onExit} />;
  }
  const validSpec = validation.spec;

  return (
    <DynamicGameRendererInner
      spec={validSpec}
      gameId={gameId}
      grade={grade}
      onComplete={onComplete}
      onExit={onExit}
      sharedFromUserId={sharedFromUserId}
    />
  );
};

interface InnerProps extends Omit<DynamicGameRendererProps, 'spec'> {
  spec: GameSpec;
}

const DynamicGameRendererInner = ({
  spec,
  gameId,
  grade,
  onComplete,
  onExit,
  sharedFromUserId,
}: InnerProps) => {
  const maxScore = useMemo(() => computeMaxScore(spec), [spec]);
  const startedAt = useRef<number>(Date.now());
  const sceneStartedAt = useRef<number>(Date.now());

  const [sceneIdx, setSceneIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(spec.rules.lives);
  const [streak, setStreak] = useState(0);
  const [streakMax, setStreakMax] = useState(0);
  const [attempts, setAttempts] = useState<SceneAttempt[]>([]);
  const [finished, setFinished] = useState(false);
  const [finalResult, setFinalResult] = useState<GameRunResult | null>(null);
  const [runKey, setRunKey] = useState(0);

  // analytics: game_play_started
  useEffect(() => {
    trackEvent('game_play_started', { game_id: gameId, grade: String(grade) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalScenes = spec.scenes.length;
  const currentScene: Scene | undefined = spec.scenes[sceneIdx];

  const finish = (finalScore: number, finalAttempts: SceneAttempt[]) => {
    if (finished) return;
    setFinished(true);
    const won = isWin(spec, finalScore, maxScore);
    const result: GameRunResult = {
      spec_version: 1,
      score: finalScore,
      max_score: maxScore,
      won,
      scene_count: totalScenes,
      scenes_correct: finalAttempts.filter((a) => a.is_correct).length,
      streak_max: streakMax,
      time_spent_ms: Date.now() - startedAt.current,
      attempts: finalAttempts,
    };
    trackEvent('game_play_completed', {
      game_id: gameId,
      score: finalScore,
      won,
      time_spent: Math.round(result.time_spent_ms / 1000),
    });
    setFinalResult(result);
    onComplete(result);
  };

  const restart = () => {
    setSceneIdx(0);
    setScore(0);
    setLives(spec.rules.lives);
    setStreak(0);
    setStreakMax(0);
    setAttempts([]);
    setFinished(false);
    setFinalResult(null);
    startedAt.current = Date.now();
    sceneStartedAt.current = Date.now();
    setRunKey((k) => k + 1);
  };

  const handleSceneAnswer = (
    isCorrect: boolean,
    details?: Record<string, unknown>,
  ) => {
    if (!currentScene || finished) return;

    const points = isCorrect ? pointsForScene(currentScene, spec.rules.scoring) : spec.rules.scoring.wrong;
    const streakBonus = isCorrect && spec.rules.scoring.bonus_streak
      ? spec.rules.scoring.bonus_streak * Math.max(0, streak)
      : 0;
    const awarded = points + streakBonus;

    const nextScore = Math.max(0, score + awarded);
    const nextStreak = isCorrect ? streak + 1 : 0;
    const nextStreakMax = Math.max(streakMax, nextStreak);
    const nextLives = isCorrect ? lives : lives - 1;

    const attempt: SceneAttempt = {
      scene_id: currentScene.id,
      scene_index: sceneIdx,
      scene_type: currentScene.type,
      is_correct: isCorrect,
      points_awarded: awarded,
      time_taken_ms: Date.now() - sceneStartedAt.current,
      details,
    };
    const nextAttempts = [...attempts, attempt];

    setScore(nextScore);
    setStreak(nextStreak);
    setStreakMax(nextStreakMax);
    setLives(nextLives);
    setAttempts(nextAttempts);

    const outOfLives = nextLives <= 0;
    const lastScene = sceneIdx >= totalScenes - 1;

    if (outOfLives || lastScene) {
      finish(nextScore, nextAttempts);
      return;
    }

    setSceneIdx((i) => i + 1);
    sceneStartedAt.current = Date.now();
  };

  if (finalResult) {
    return (
      <ResultScreen
        gameId={gameId}
        result={finalResult}
        gameTitle={spec.meta.title}
        onPlayAgain={restart}
        onExit={onExit}
        sharedFromUserId={sharedFromUserId ?? null}
      />
    );
  }

  if (!currentScene) {
    return <UnplayableNotice errors={['No scenes available']} onExit={onExit} />;
  }

  const progress = Math.round(((sceneIdx) / totalScenes) * 100);

  return (
    <div key={runKey} className="space-y-4">
      {/* HUD */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div className="flex items-center gap-2">
              <Badge variant="secondary">{spec.theme.palette}</Badge>
              <Badge variant="outline">
                Scene {Math.min(sceneIdx + 1, totalScenes)} / {totalScenes}
              </Badge>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <span className="flex items-center gap-1">
                <Trophy className="h-4 w-4" /> {score} / {maxScore}
              </span>
              <span className="flex items-center gap-1">
                <Heart className="h-4 w-4 text-destructive" /> {lives}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" /> {spec.rules.time_limit_seconds ?? '∞'}s
              </span>
              {streak > 1 && <Badge>🔥 {streak}</Badge>}
            </div>
          </div>
          <Progress value={progress} className="mt-3 h-2" />
        </CardContent>
      </Card>

      {/* Scene */}
      <motion.div
        key={currentScene.id}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        <SceneRouter scene={currentScene} onAnswer={handleSceneAnswer} />
      </motion.div>

      {onExit && (
        <div className="flex justify-end">
          <Button variant="ghost" size="sm" onClick={onExit}>
            Exit game
          </Button>
        </div>
      )}
    </div>
  );
};

export default DynamicGameRenderer;
